import { useEffect } from "react";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import { depositLimit as depositLimitModel } from "@/hooks/firestore/models/depositLimit";
import { message } from "utilities/message/message";
import { isPast, parseISO } from "date-fns";
import type { Currency } from "../models/currency";
import type { Always } from "types/utilities";

const depositLimitsModel = depositLimitModel.array();

const useDepositLimits = (currency: string) => {
  const {
    isLoading,
    data: limitsForAllCurrencies,
    error,
  } = useFirestore<typeof depositLimitsModel, "currency", Currency>(
    `/users/{userId}/depositLimits`,
    depositLimitsModel,
    {
      keyPath: "currency",
    },
  );

  const depositLimit = (limitsForAllCurrencies || []).find(
    (limit) => limit.currency === currency,
  );

  useEffect(() => {
    if (!depositLimit || !depositLimit.expiresAt) return;

    if (isPast(parseISO(depositLimit.expiresAt))) {
      // we do this to trigger limits update on backend
      // this makes sure that backend and firestore never gets out of sync
      message("get-deposit-limits", {});
    }
  }, [depositLimit]);

  return { depositLimit, error, isLoading };
};

type DepositLimit = Always<ReturnType<typeof useDepositLimits>["depositLimit"]>;

export { useDepositLimits, type DepositLimit };
